import React, { useMemo, useState } from "react";
import { groupPathways } from "./needsApplicationModel";
import ConfigurationPrimaryExplanation from "./ConfigurationPrimaryExplanation";
import TerrainRadialOverview from "./TerrainRadialOverview";
import TerrainFieldMap from "./TerrainFieldMap";
import TerrainAnalogyLegend from "./TerrainAnalogyLegend";

export default function TerrainConfigurationExperience({
  capabilityLabel,
  state,
  pathways,
  comparisonCount,
  onInspect,
  onCompare,
}) {
  const groups = useMemo(
    () => groupPathways(pathways || []),
    [pathways]
  );

  const [selectedKey, setSelectedKey] = useState(null);
  const [hoverKey, setHoverKey] = useState(null);
  const [showTerrain, setShowTerrain] = useState(false);

  const focusKey =
    hoverKey ||
    selectedKey ||
    null;

  function inspect(group) {
    if (!group) return;
    setSelectedKey(group.key);
    onInspect?.(group);
  }

  return (
    <div className="rr-terrain-experience rr-radial-primary-experience">
      <ConfigurationPrimaryExplanation
        state={state}
        groups={groups}
      />

      <section className="rr-primary-configuration-stage">
        <div className="rr-primary-stage-heading">
          <div>
            <span className="v25a-eyebrow">1 · UNDERSTAND ONE CONFIGURATION</span>
            <h2>Start with the configured pathways</h2>
          </div>

          <p>
            The capability remains fixed in the center. Each surrounding glade
            is a configured pathway group. Read the whole pattern first; zoom
            into a pathway only when you want to know why it is there.
          </p>
        </div>

        <TerrainRadialOverview
          capabilityLabel={capabilityLabel}
          groups={groups}
          state={state}
          focusKey={focusKey}
          onFocus={setHoverKey}
          onBlur={() => setHoverKey(null)}
          onInspect={inspect}
        />
      </section>

      <section className="rr-projection-lens-entry rr-projection-lens-primary">
        <div className="rr-projection-step-number">2</div>

        <div className="rr-projection-lens-icon" aria-hidden="true">
          <i />
          <span />
          <i />
        </div>

        <div className="rr-projection-lens-copy">
          <span className="v25a-eyebrow">NEXT · PROJECTION LENS</span>
          <h3>Compare another configuration around the same capability</h3>
          <p>
            Keep the stable center fixed. Compare another warranted
            configuration and inspect which pathways, support patterns and
            resultants differ.
          </p>
        </div>

        <button
          type="button"
          onClick={onCompare}
          disabled={!comparisonCount || typeof onCompare !== "function"}
        >
          <span>
            {comparisonCount || 0} warranted comparison
            {comparisonCount === 1 ? "" : "s"}
          </span>
          Open Projection Lens →
        </button>
      </section>

      <section className={`rr-later-terrain-stage ${showTerrain ? "open" : ""}`}>
        <header>
          <div className="rr-later-stage-number">3</div>

          <div>
            <span className="v25a-eyebrow">LATER · SPATIAL INTERPRETATION</span>
            <h3>Relational terrain</h3>
            <p>
              Once the configuration itself is clear, the same pathways can be
              rendered as glades, friction ridges, forks and fog. This is a
              secondary visual interpretation, not another analytical object.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setShowTerrain((value) => !value)}
          >
            {showTerrain ? "Hide terrain ↑" : "Reveal terrain ↓"}
          </button>
        </header>

        {showTerrain ? (
          <div className="rr-later-terrain-content">
            <TerrainFieldMap
              capabilityLabel={capabilityLabel}
              groups={groups}
              focusKey={focusKey}
              onFocus={setHoverKey}
              onBlur={() => setHoverKey(null)}
              onInspect={inspect}
            />

            <TerrainAnalogyLegend />
          </div>
        ) : (
          <div className="rr-later-terrain-placeholder">
            <span>
              terrain = optional spatial reading of the already-understood
              configuration
            </span>
          </div>
        )}
      </section>
    </div>
  );
}
